import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIsInstitutionDetailsUpdate } from '@/features/institution/context/update';
import { getDetailsKeyIndex, INSTITUTION_DETAILS_TYPE, type InstitutionDetailsKeyForDetailsType, type InstitutionDetailsType } from '@/features/institution/utils';
import { useDetailsNext } from './useDetailsNext';

const DETAILS_TYPE_NEXT_ROUTE_MAP = {
  [INSTITUTION_DETAILS_TYPE.ONBOARDING]: '/institution/onboarding/verification',
  [INSTITUTION_DETAILS_TYPE.FUTURES]: '/institution/futures/client-categorisation/treatment',
};

export const useDetailsFlowControl = <T extends InstitutionDetailsType>(detailType: T, currentKey: InstitutionDetailsKeyForDetailsType<T>) => {
  const isUpdate = useIsInstitutionDetailsUpdate();
  const { isLoading: isLoadingDetailsNext, data: detailsNext, version } = useDetailsNext(detailType);
  const navigate = useNavigate();
  const [isPending, setIsPending] = useState(true);
  const isLoading = isLoadingDetailsNext || isPending;

  useEffect(() => {
    if (isLoadingDetailsNext || !version || isPending === false) return;

    if (!detailsNext) {
      // As after signing the draft, the initial redirect works out what an upgrading firm needs next
      navigate(isUpdate ? '/institution/onboarding' : DETAILS_TYPE_NEXT_ROUTE_MAP[detailType], { replace: true });
    } else if (detailsNext.key !== currentKey && detailsNext.route) {
      const currentKeyIndex = getDetailsKeyIndex(detailType, version, currentKey);
      const nextKeyIndex = getDetailsKeyIndex(detailType, version, detailsNext.key);

      if (currentKeyIndex > nextKeyIndex) {
        navigate(detailsNext.route, { replace: true });
      }
    }

    setIsPending(false);
  }, [currentKey, detailsNext, detailType, isLoadingDetailsNext, isPending, isUpdate, navigate, version]);

  return { isLoading };
};
